/**
 * Lifecycle of a friendship edge as stored in the database. Mirrors the Prisma
 * `FriendshipStatus` enum. A single edge per unordered user pair carries the
 * whole mutual-friend model: PENDING (a request), ACCEPTED (mutual friends), or
 * BLOCKED (the edge's requester has blocked its addressee).
 */
export const FRIENDSHIP_STATUSES = ["PENDING", "ACCEPTED", "BLOCKED"] as const;
export type FriendshipStatus = (typeof FRIENDSHIP_STATUSES)[number];

/**
 * How the caller relates to another user, from the caller's point of view. This
 * is the client-facing projection of a friendship edge (or its absence):
 * - `none`     — no relationship.
 * - `pending`  — an outstanding friend request (see `FriendDirection`).
 * - `accepted` — mutual friends.
 * - `blocked`  — the caller has blocked the other user (only ever surfaced to
 *   the blocker; a user blocked by someone else never sees that relationship).
 */
export const FRIEND_RELATIONS = ["none", "pending", "accepted", "blocked"] as const;
export type FriendRelation = (typeof FRIEND_RELATIONS)[number];

/**
 * Direction of a `pending` or `blocked` relationship relative to the caller:
 * - `outgoing` — the caller initiated it (sent the request / issued the block).
 * - `incoming` — the other user initiated it (the caller received the request).
 * - `none`     — not applicable (`accepted` / `none` relationships).
 */
export const FRIEND_DIRECTIONS = ["incoming", "outgoing", "none"] as const;
export type FriendDirection = (typeof FRIEND_DIRECTIONS)[number];

/** Minimum characters before a user search runs (below this the client shows a hint and
 *  the server returns []). Shared so the web + api agree. */
export const SEARCH_MIN_QUERY_LENGTH = 4;

/**
 * The canonical members string for a DM pair: the two app user ids, sorted, `a:b`. Both peers
 * compute the same string regardless of who opens the thread.
 *
 * The OPAQUE matchmaking key is `SHA-256(dmMembers(...))` — see the web `dmRoomKey` (SubtleCrypto)
 * and the server `dmRoomKey` (`@bing/shared/server`, node:crypto); both are standard SHA-256, so
 * they agree. DM rooms rendezvous via Colyseus `filterBy`, whose fields are published in the
 * public matchmake listing — keying on the raw pair leaked the social graph (who-messages-whom)
 * to anyone enumerating `GET /matchmake/dm`. The hash keeps rendezvous deterministic while the
 * listing reveals nothing reversible; the raw pair rides as a NON-filter option and the server
 * verifies it hashes to the key (blocking a griefer from squatting a pair's key).
 */
export function dmMembers(selfUserId: string, peerUserId: string): string {
  return [selfUserId, peerUserId].sort().join(":");
}

/** The identity fields any user-facing name resolution reads (a structural
 * subset of `FriendView` / the api's `User` row / the web `Profile`). */
export interface DisplayNameParts {
  nickname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  appUsername?: string | null;
}

/**
 * THE user-facing name resolution, shared by every surface that labels a person
 * (api bot notifications, live-push actor names, the web's lists/toasts/chat):
 *   1. the chosen in-game `nickname`;
 *   2. the Telegram display name `firstName [lastName]`;
 *   3. the app handle (`appUsername`) WITHOUT any `@` — a last resort so a
 *      handle-only user still reads as themselves.
 * Returns null when there is truly no name data (callers pick their own neutral
 * placeholder, e.g. «کاربر»). It NEVER considers the Telegram `@username` or any
 * numeric id — those are identifiers, not names, and must not leak into copy.
 */
export function displayNameOf(u: DisplayNameParts): string | null {
  const nick = u.nickname?.trim();
  if (nick) return nick;
  const full = [u.firstName, u.lastName]
    .map((s) => s?.trim())
    .filter((s): s is string => !!s)
    .join(" ");
  if (full) return full;
  const handle = u.appUsername?.trim();
  if (handle) return handle;
  return null;
}
